import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Settings, Shield, AlertCircle, CheckCircle2, Lock } from 'lucide-react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PaymentMethodsPanel from './components/PaymentMethodsPanel';
import BillingContactsPanel from './components/BillingContactsPanel';
import SubscriptionPreferencesPanel from './components/SubscriptionPreferencesPanel';
import { supabase } from '../../lib/supabase';


export default function PaymentBillingSettings() {
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [organizationType, setOrganizationType] = useState(null);

  useEffect(() => {
    checkOrganizationType();
  }, []);

  const checkOrganizationType = async () => {
    try {
      setLoading(true);
      const { data: user } = await supabase?.auth?.getUser();
      
      if (user?.user?.id) {
        const { data: companySettings, error: settingsError } = await supabase
          ?.from('company_settings')
          ?.select('organization_type')
          ?.eq('user_id', user?.user?.id)
          ?.single();

        if (settingsError) {
          console.error('Error fetching company settings:', settingsError);
          setIsReadOnly(true); // Default to read-only on error
          return;
        }

        const orgType = companySettings?.organization_type;
        setOrganizationType(orgType);

        // Only Pacific RBS Ltd (public sector) can edit
        // Clients (private sector) have read-only access
        setIsReadOnly(orgType === 'private');
      }
    } catch (err) {
      console.error('Error checking organization type:', err);
      setIsReadOnly(true); // Default to read-only on error
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (isReadOnly) {
      setError('You do not have permission to modify payment and billing settings. Please contact Pacific RBS Ltd.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      // Simulate save operation
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Payment & Billing Settings - PNG HR Compliance</title>
        <meta
          name="description"
          content="Manage payment methods, billing contacts, and subscription preferences for your company"
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Settings className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-heading font-bold text-foreground">
                      Payment & Billing Settings
                    </h1>
                    {isReadOnly && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-warning/10 border border-warning/20 rounded">
                        <Lock className="h-4 w-4 text-warning" />
                        <span className="text-xs font-medium text-warning">Read Only</span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {isReadOnly 
                      ? 'View payment methods, billing contacts, and subscription preferences (managed by Pacific RBS Ltd)'
                      : 'Configure payment methods, billing contacts, and subscription preferences'
                    }
                  </p>
                </div>
              </div>
              {!isReadOnly && (
                <Button
                  onClick={handleSaveSettings}
                  loading={saving}
                  iconName="Save"
                  disabled={saving}
                >
                  Save Changes
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Read-Only Notice for Clients */}
          {isReadOnly && (
            <div className="mb-6 p-4 bg-warning/10 border border-warning/20 rounded-lg flex items-start gap-3">
              <Lock className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-warning">Read-Only Access</h3>
                <p className="text-sm text-warning/90 mt-1">
                  This section is managed by Pacific RBS Ltd. You can view the current settings but cannot make changes.
                  To update payment methods, billing contacts, or subscription preferences, please contact Pacific RBS Ltd at{' '}
                  <a href="mailto:admin@hrpacificrbs.com" className="underline font-medium">admin@hrpacificrbs.com</a> or{' '}
                  <a href="tel:+67578808282" className="underline font-medium">+675 78808282</a>.
                </p>
              </div>
            </div>
          )}

          {/* Alert Messages */}
          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-destructive">Error</h3>
                <p className="text-sm text-destructive/90 mt-1">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-success">Success</h3>
                <p className="text-sm text-success/90 mt-1">
                  Payment and billing settings saved successfully
                </p>
              </div>
            </div>
          )}

          {/* Security & Compliance Banner */}
          <div className="mb-6 bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-foreground">Secure Payment Processing</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  All payment information is encrypted and PCI DSS compliant. Your financial data is
                  protected with industry-standard security measures.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Lock" size={16} className="text-success" />
                <span className="text-xs font-medium text-success">Verified</span>
              </div>
            </div>
          </div>

          {/* Three Column Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Payment Methods - 4 columns */}
            <div className="lg:col-span-4">
              <PaymentMethodsPanel isReadOnly={isReadOnly} />
            </div>

            {/* Billing Contacts - 4 columns */}
            <div className="lg:col-span-4">
              <BillingContactsPanel isReadOnly={isReadOnly} />
            </div>

            {/* Subscription Preferences - 4 columns */}
            <div className="lg:col-span-4">
              <SubscriptionPreferencesPanel isReadOnly={isReadOnly} />
            </div>
          </div>

          {/* Audit Trail Notice */}
          <div className="mt-6 p-4 bg-muted/30 border border-border rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="FileText" size={18} className="text-muted-foreground flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-foreground">Audit Trail Logging</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  All changes to payment methods, billing contacts, and subscription preferences are
                  logged for compliance and security purposes. View complete audit history in the{' '}
                  <a href="/audit-trail" className="text-primary hover:underline">
                    Audit Trail
                  </a>{' '}
                  section.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}